// ============================================================================
// Sparkline + AreaSpark — lightweight inline-SVG charts.
//
// We already use recharts elsewhere, but those are full chart components
// with axes / tooltips / responsive containers. These primitives are for
// the inline 1-line "vibe" charts that show up inside cards and rows —
// no axes, no labels, no DOM cost.
//
// Both work off a flat number[] data array. AreaSpark fills the gradient
// area under the line and uses 100% width via viewBox, so it stretches
// to fit any container (the portfolio sparkline, the SPY 30-day chart).
// ============================================================================

export function Sparkline({ data, w = 120, h = 32, color = 'var(--accent)', strokeW = 1.6 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - ((v - min) / rng) * (h - 4) - 2,
  ]);
  const d = pts.map(([x, y], i) => (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function AreaSpark({ data, w = 240, h = 64, color = 'var(--green)', id = 'sp' }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - ((v - min) / rng) * (h - 6) - 3,
  ]);
  const line = pts.map(([x, y], i) => (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' ');
  const area = line + ` L${w} ${h} L0 ${h} Z`;
  // Unique gradient id per render so multiple AreaSparks on the page don't
  // bleed each other's colour stops.
  const gid = `${id}-${Math.abs((color + '').split('').reduce((a, c) => a + c.charCodeAt(0), 0))}`;
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
